import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import StarIcon from '@mui/icons-material/Star';

// Стили для различных типов блоков
const calloutTypes = {
  info: {
    name: 'Информация',
    icon: InfoIcon,
    color: '#2196f3',
    backgroundColor: '#e3f2fd',
    borderColor: '#2196f3',
  },
  warning: {
    name: 'Предупреждение',
    icon: WarningIcon,
    color: '#ff9800',
    backgroundColor: '#fff3e0',
    borderColor: '#ff9800',
  },
  error: {
    name: 'Ошибка',
    icon: ErrorIcon,
    color: '#f44336',
    backgroundColor: '#ffebee',
    borderColor: '#f44336',
  },
  success: {
    name: 'Успех',
    icon: CheckCircleIcon,
    color: '#4caf50',
    backgroundColor: '#e8f5e8',
    borderColor: '#4caf50',
  },
  tip: {
    name: 'Совет',
    icon: LightbulbIcon,
    color: '#ffc107',
    backgroundColor: '#fffde7',
    borderColor: '#ffc107',
  },
  important: {
    name: 'Важно',
    icon: StarIcon,
    color: '#9c27b0',
    backgroundColor: '#f3e5f5',
    borderColor: '#9c27b0',
  },
};

// Стилизованная карточка для предпросмотра
const StyledCalloutCard = styled(Card)(({ theme, calloutType }) => {
  const type = calloutTypes[calloutType];
  return {
    border: `2px solid ${type.borderColor}`,
    backgroundColor: type.backgroundColor,
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  };
});

const CalloutPicker = ({ open, onClose, onCalloutSelect }) => {
  const [selectedType, setSelectedType] = useState('info');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const handleTypeSelect = (type) => {
    setSelectedType(type);
  };

  const handleInsert = () => {
    const callout = calloutTypes[selectedType];
    const IconComponent = callout.icon;
    
    // Создаем HTML для блока callout
    const calloutHtml = `
      <div style="
        border: 2px solid ${callout.borderColor};
        border-radius: 8px;
        background-color: ${callout.backgroundColor};
        padding: 16px;
        margin: 16px 0;
        display: flex;
        align-items: flex-start;
        gap: 12px;
      ">
        <div style="
          color: ${callout.color};
          font-size: 24px;
          line-height: 1;
          margin-top: 2px;
        ">
          ${getIconSvg(selectedType)}
        </div>
        <div style="flex: 1;">
          ${title ? `<div style="
            font-weight: 600;
            color: ${callout.color};
            margin-bottom: 8px;
            font-size: 16px;
          ">${title}</div>` : ''}
          <div style="
            color: #333;
            line-height: 1.6;
          ">${content || 'Введите текст блока...'}</div>
        </div>
      </div>
    `;

    onCalloutSelect(calloutHtml);
    handleClose();
  };

  const handleClose = () => {
    setTitle('');
    setContent('');
    setSelectedType('info');
    onClose();
  };

  // Функция для получения SVG иконки
  const getIconSvg = (type) => {
    const iconMap = {
      info: '&#8505;',
      warning: '&#9888;',
      error: '&#10060;',
      success: '&#9989;',
      tip: '&#128161;',
      important: '&#11088;',
    };
    return iconMap[type] || '&#8505;';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Создать информационный блок
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Выберите тип блока:
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(calloutTypes).map(([key, type]) => {
              const IconComponent = type.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <StyledCalloutCard
                    calloutType={key}
                    onClick={() => handleTypeSelect(key)}
                    sx={{
                      opacity: selectedType === key ? 1 : 0.7,
                      transform: selectedType === key ? 'scale(1.02)' : 'scale(1)',
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconComponent sx={{ color: type.color }} />
                        <Typography variant="body2" sx={{ color: type.color, fontWeight: 600 }}>
                          {type.name}
                        </Typography>
                      </Box>
                    </CardContent>
                  </StyledCalloutCard>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Заголовок (необязательно)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Содержимое блока"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            rows={3}
            required
          />
        </Box>

        {/* Предпросмотр */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Предпросмотр:
          </Typography>
          <Box
            sx={{
              border: `2px solid ${calloutTypes[selectedType].borderColor}`,
              borderRadius: 1,
              backgroundColor: calloutTypes[selectedType].backgroundColor,
              p: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            <Box sx={{ color: calloutTypes[selectedType].color, fontSize: '24px', lineHeight: 1, mt: 0.25 }}>
              {React.createElement(calloutTypes[selectedType].icon)}
            </Box>
            <Box sx={{ flex: 1 }}>
              {title && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: calloutTypes[selectedType].color,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {title}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6 }}>
                {content || 'Введите текст блока...'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          Отмена
        </Button>
        <Button 
          onClick={handleInsert} 
          variant="contained"
          disabled={!content.trim()}
        >
          Вставить блок
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalloutPicker;
